import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Lock, ShieldCheck } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { authApi } from '@/features/auth/api/auth.api';
import { PasswordStrength } from '@/features/auth/components/PasswordStrength';
import {
  changePasswordSchema,
  type ChangePasswordValues,
} from '@/features/auth/schemas/password.schema';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';

/**
 * Changing one's own password, from My Profile.
 *
 * The same rules, the same request and the same strength meter as the
 * administrators' Change password page — only the frame differs. A successful
 * change ends every session, this one included, so the teacher signs in again
 * with the new password, exactly as on that page.
 */
export function TeacherSettingsTab() {
  const navigate = useNavigate();
  const clearSession = useAuthStore((state) => state.clearSession);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = form;
  useLiveFormErrors(form);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.changePassword(values);
      clearSession();
      navigate('/login', { replace: true, state: { passwordChanged: true } });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        applyFieldErrors(setError, error);
        setFormError(error.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={onSubmit} noValidate className="card px-6 py-4 space-y-5">
        <div className="flex items-center gap-3 border-b border-line pb-4">
          <Lock className="w-6 h-6 text-indigo-600 dark:text-indigo-400" aria-hidden />
          <h3 className="text-lg font-black text-ink">Security & Password</h3>
        </div>

        {formError && (
          <Alert tone="critical" title="Could not change your password">
            {formError}
          </Alert>
        )}

        <fieldset disabled={isSubmitting} className="flex flex-col gap-4">
          <Input
            label="Current password"
            autoComplete="current-password"
            isPassword
            leftIcon={<Lock className="h-4 w-4" />}
            error={errors.currentPassword?.message}
            {...register('currentPassword')}
          />
          <Input
            label="New password"
            autoComplete="new-password"
            isPassword
            leftIcon={<KeyRound className="h-4 w-4" />}
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />
          <Input
            label="Confirm new password"
            autoComplete="new-password"
            isPassword
            leftIcon={<ShieldCheck className="h-4 w-4" />}
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
        </fieldset>

        <div>
          <Button type="submit" isLoading={isSubmitting}>
            Update password
          </Button>
        </div>
      </form>

      <div className="card px-6 py-4 space-y-4">
        <div className="flex items-center gap-3 border-b border-line pb-4">
          <ShieldCheck className="w-6 h-6 text-indigo-600 dark:text-indigo-400" aria-hidden />
          <h3 className="text-lg font-black text-ink">A strong password</h3>
        </div>
        <PasswordStrength
          value={watch('newPassword') ?? ''}
          confirmation={watch('confirmPassword') ?? ''}
        />
        <p className="text-sm text-ink-2">
          Changing it signs you out everywhere, including here. Sign in again with the new password.
        </p>
      </div>
    </div>
  );
}
